import React, { useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import { useFileUpload } from '@/hooks/useFileUpload';
import {
  Upload, Download, FileSpreadsheet, File, X, CheckCircle,
  AlertCircle, ArrowRight, Play, Info, Database,
} from 'lucide-react';

const sampleFiles = [
  { name: 'Client Database Sample', description: 'General client records with addresses', icon: FileSpreadsheet },
  { name: 'RealPage Claims Sample', description: 'Formatted for RealPage litigation matching', icon: FileSpreadsheet },
  { name: 'Mass Tort Sample', description: 'PFAS/AFFF exposure claims format', icon: FileSpreadsheet },
];

const columnMappings = [
  { source: 'Client_Name', target: 'Full Name', status: 'mapped' },
  { source: 'Email', target: 'Email Address', status: 'mapped' },
  { source: 'Current_Address', target: 'Street Address', status: 'mapped' },
  { source: 'City', target: 'City', status: 'mapped' },
  { source: 'State', target: 'State', status: 'mapped' },
  { source: 'ZIP', target: 'ZIP Code', status: 'mapped' },
  { source: 'Date_Of_Birth', target: 'Date of Birth', status: 'mapped' },
  { source: 'Occupation', target: 'Occupation', status: 'mapped' },
];

const UploadDatabase: React.FC = () => {
  const { file, fileName, fileSize, progress, status, error, fileInputRef, handleDrop, handleInputChange, upload, reset } = useFileUpload();
  const [currentStep, setCurrentStep] = useState(1);
  const [jobType, setJobType] = useState('RealPage');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleStartProcessing = useCallback(async () => {
    setIsProcessing(true);
    try {
      await upload();
      setCurrentStep(3);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [upload]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <ContentWrapper>
      <PageHeader
        title="Bulk Claims Discovery for Client Databases"
        subtitle="For complete instructions for using this tool, please see the documentation."
        breadcrumbs={[
          { label: 'Claims Discovery' },
          { label: 'Upload Database', isActive: true },
        ]}
      />

      {/* Step Indicator */}
      <div className="flex items-center gap-4 mb-8">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step === currentStep
                  ? 'bg-blue-600 text-white'
                  : step < currentStep
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step < currentStep ? <CheckCircle className="h-5 w-5" /> : step}
            </div>
            <span className={`text-sm ${step === currentStep ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
              {step === 1 ? 'Upload File' : step === 2 ? 'Map Columns' : 'Process'}
            </span>
            {step < 3 && <ArrowRight className="h-4 w-4 text-gray-300 mx-2" />}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Upload Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Upload */}
          {currentStep === 1 && (
            <Card className="bg-white border border-gray-200 shadow-sm">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="text-lg text-gray-900">Upload your file</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {!file ? (
                  <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => e.preventDefault()}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      Drop file with client data here
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Excel or CSV (up to 25K leads per batch are supported)
                    </p>
                    <Button variant="outline">Upload File</Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleInputChange}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <FileSpreadsheet className="h-10 w-10 text-green-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{fileName}</p>
                        <p className="text-sm text-gray-500">{formatFileSize(fileSize)}</p>
                      </div>
                      <button onClick={reset} className="p-1 hover:bg-green-100 rounded">
                        <X className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>

                    {/* Job Type Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Claims Type</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['RealPage', 'PFAS', 'General', 'Full Overlap Scan'].map((type) => (
                          <button
                            key={type}
                            onClick={() => setJobType(type)}
                            className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                              jobType === type
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-600'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => setCurrentStep(2)}
                    >
                      Continue to Column Mapping <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}

                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 2: Column Mapping */}
          {currentStep === 2 && (
            <Card className="bg-white border border-gray-200 shadow-sm">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-gray-900">Column Mapping</CardTitle>
                  <Badge variant="success">Auto-detected</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-sm text-gray-500 mb-4">
                  We've automatically mapped your columns. Please verify or adjust the mappings below.
                </p>
                <div className="space-y-3">
                  {columnMappings.map((mapping, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700">{mapping.source}</span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-400" />
                      <div className="flex-1">
                        <select className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5" defaultValue={mapping.target}>
                          <option>{mapping.target}</option>
                          <option>-- Unmapped --</option>
                        </select>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    Back
                  </Button>
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={handleStartProcessing}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Starting Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Play className="h-4 w-4" /> Start Processing
                      </span>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Processing Started */}
          {currentStep === 3 && (
            <Card className="bg-white border border-gray-200 shadow-sm">
              <CardContent className="p-12 text-center">
                <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Started!</h2>
                <p className="text-gray-500 mb-6">
                  Your file "{fileName}" has been submitted for {jobType} claims processing.
                  You'll be notified when results are ready.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => { reset(); setCurrentStep(1); }}>
                    Upload Another File
                  </Button>
                  <a href="/claims/processing">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      View Processing Jobs <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Sample Downloads & Info */}
        <div className="space-y-6">
          <Card className="bg-white border border-gray-200 shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base text-gray-900">Download sample lists</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {sampleFiles.map((sample, index) => {
                  const Icon = sample.icon;
                  return (
                    <button
                      key={index}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors text-left"
                    >
                      <Icon className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{sample.name}</p>
                        <p className="text-xs text-gray-500">{sample.description}</p>
                      </div>
                      <Download className="h-4 w-4 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900 mb-1">Processing Info</p>
                  <ul className="space-y-1 text-blue-700">
                    <li>Excel and CSV formats supported</li>
                    <li>Up to 25,000 leads per batch</li>
                    <li>1 credit = 1 record processed</li>
                    <li>Results typically ready in minutes</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Database className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-900">Credit Balance</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">2,847</p>
              <p className="text-xs text-gray-500 mt-1">credits available</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </ContentWrapper>
  );
};

export default UploadDatabase;